import { useCallback, useEffect, useRef } from "react";
import {
  CompanionStreamingService,
  StreamState,
} from "@service/companion-streaming.service";
import { TextBuffer } from "@service/text-buffer";
import { AudioMessageStream } from "@models/audio-message-stream";
import { removeMarkdown } from "@utils/text.util";
import { useDispatch } from "react-redux";
import { addText, setStreamState, setText } from "@store/avatarSlice";

export const useCompanionStreamingService = (
  speak: (text: string) => void,
  evictFullSentence = true,
): {
  send: (message: AudioMessageStream) => Promise<void>;
} => {
  const streamingService = useRef<CompanionStreamingService | null>(null);
  const textBuffer = useRef<TextBuffer | null>(null);
  const dispatch = useDispatch();

  const cleanSpeak = useCallback(
    (text: string): void => {
      const cleanText = removeMarkdown(text, {});
      speak(cleanText);
      dispatch(addText(cleanText));
    },
    [dispatch, speak],
  );

  const handleStateChange = useCallback(
    (streamState: StreamState) => {
      dispatch(setStreamState(streamState));
    },
    [dispatch],
  );

  const handleResponse = useCallback((text: string) => {
    if (!textBuffer.current) {
      return;
    }
    textBuffer.current.addText(text);
  }, []);

  useEffect(() => {
    const service = new CompanionStreamingService(
      handleResponse,
      handleStateChange,
    );
    streamingService.current = service;

    return () => {
      service?.destroy();
    };
  }, [handleResponse, handleStateChange]);

  useEffect(() => {
    const buffer = new TextBuffer(cleanSpeak, evictFullSentence);
    textBuffer.current = buffer;

    return () => {
      buffer.close();
    };
  }, [cleanSpeak, evictFullSentence, dispatch]);

  const send = useCallback(
    async (message: AudioMessageStream): Promise<void> => {
      if (!streamingService.current) {
        return;
      }
      dispatch(setText(null));
      dispatch(setStreamState(null));
      await streamingService.current.sendMessage(message);
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(setStreamState(null));
      dispatch(setText(null));
    };
  }, [dispatch]);

  return { send };
};
